import type { OperationVariables, QueryHookOptions, QueryResult, TypedDocumentNode } from '@apollo/client';
import { isNil } from 'es-toolkit';

import type { UsePollResult } from './usePoll';
import { usePoll } from './usePoll';
import { useQueryWithErrorLogging } from './useQueryWithErrorLogging';

type PollLimitOptions = {
  maxPollTime?: number;
  maxPollCount?: number;
  onPollingExpired?: () => void;
};

type UseQueryWithPollingOptions<TData, TVariables extends OperationVariables> = QueryHookOptions<TData, TVariables> &
  PollLimitOptions;

type UseQueryWithPollingResult<TData, TVariables extends OperationVariables> = QueryResult<TData, TVariables> &
  UsePollResult;

export function useQueryWithPolling<TVariables extends OperationVariables, TData>(
  query: TypedDocumentNode<TData, TVariables>,
  options?: UseQueryWithPollingOptions<TData, TVariables>,
): UseQueryWithPollingResult<TData, TVariables> {
  const { maxPollTime, maxPollCount, pollInterval, onPollingExpired, ...queryOptions } = options ?? {};

  const queryResult = useQueryWithErrorLogging(query, {
    ...queryOptions,
    pollInterval: 0,
  });

  const { pollCount, isPolling, startPolling, stopPolling, pollingExpired, reset } = usePoll(
    () => queryResult.refetch(),
    {
      interval: pollInterval,
      maxCount: maxPollCount,
      maxTime: maxPollTime,
      enabled: Boolean(!isNil(pollInterval) && pollInterval > 0 && (isNil(queryOptions.skip) || !queryOptions.skip)),
      onPollingExpired,
    },
  );

  return {
    ...queryResult,
    pollCount,
    isPolling,
    startPolling,
    stopPolling,
    pollingExpired,
    reset,
  };
}
